import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import type { Plugin } from 'chart.js';
import type { ScorecardRow } from '../types';

/** Draws the data value at the end of each horizontal bar. */
function valueLabelPlugin(format: (v: number) => string): Plugin<'bar'> {
  return {
    id: 'barValueLabels',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      chart.data.datasets.forEach((dataset, di) => {
        const meta = chart.getDatasetMeta(di);
        meta.data.forEach((bar, i) => {
          const val = dataset.data[i] as number | null | undefined;
          if (val === null || val === undefined) return;
          ctx.save();
          ctx.font = 'bold 11px Inter, Arial, sans-serif';
          ctx.fillStyle = '#1a3a5c';
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'left';
          ctx.fillText(format(val), bar.x + 6, bar.y);
          ctx.restore();
        });
      });
    },
  };
}

const callsLabelPlugin = valueLabelPlugin((v) => String(v));
const rateLabelPlugin = valueLabelPlugin((v) => `${v}%`);

type Props = {
  rows: ScorecardRow[];
};

function statusColor(status: ScorecardRow['status']): string {
  if (status === 'red') return '#B91C1C';
  if (status === 'yellow') return '#B45309';
  return '#1a3a5c';
}

function answerRateColor(rate: number): string {
  if (rate >= 92) return '#047857';
  if (rate >= 85) return '#B45309';
  return '#B91C1C';
}

export default function FbcPortfolioCharts({ rows }: Props) {
  const sorted = useMemo(
    () => rows.slice().sort((a, b) => b.totalCalls - a.totalCalls),
    [rows],
  );

  if (sorted.length === 0) return null;

  const labels = sorted.map((r) => r.name || r.afNumber);
  const calls = sorted.map((r) => r.totalCalls);
  const rates = sorted.map((r) => r.answerRate);
  const callBarColors = sorted.map((r) => statusColor(r.status));
  const rateBarColors = sorted.map((r) => answerRateColor(r.answerRate));

  // ~36px per row to keep horizontal bars readable
  const chartHeight = Math.max(300, sorted.length * 36);

  const baseOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { bodyFont: { size: 11 } },
    },
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 28,
      }}
    >
      <div className="ds-panel" style={{ padding: '22px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3a5c', marginBottom: 14 }}>
          Calls by Franchisee
        </div>
        <div style={{ position: 'relative', height: chartHeight }}>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  data: calls,
                  backgroundColor: callBarColors,
                  borderRadius: 0,
                  barPercentage: 0.7,
                },
              ],
            }}
            options={{
              ...baseOptions,
              layout: { padding: { right: 40 } },
              scales: {
                x: {
                  beginAtZero: true,
                  grid: { color: '#E1E8F0' },
                  ticks: { font: { size: 9 }, color: '#475569' },
                },
                y: {
                  grid: { display: false },
                  ticks: { font: { size: 11 }, color: '#0F1729' },
                },
              },
            }}
            plugins={[callsLabelPlugin]}
          />
        </div>
      </div>
      <div className="ds-panel" style={{ padding: '22px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3a5c', marginBottom: 14 }}>
          Answer Rate by Franchisee
        </div>
        <div style={{ position: 'relative', height: chartHeight }}>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  data: rates,
                  backgroundColor: rateBarColors,
                  borderRadius: 0,
                  barPercentage: 0.7,
                },
              ],
            }}
            options={{
              ...baseOptions,
              layout: { padding: { right: 50 } },
              scales: {
                x: {
                  beginAtZero: true,
                  max: 100,
                  grid: { color: '#E1E8F0' },
                  ticks: {
                    font: { size: 9 },
                    color: '#475569',
                    callback: (v) => `${v}%`,
                  },
                },
                y: {
                  grid: { display: false },
                  ticks: { font: { size: 11 }, color: '#0F1729' },
                },
              },
            }}
            plugins={[rateLabelPlugin]}
          />
        </div>
      </div>
    </div>
  );
}
